import React, { FC, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import useStyles from './styles';
import { intersection, not, union } from '../../logics';

interface ICustomList {
  title: string;
  items: any[];
  values?: any[];
  onChange?: (values: any[]) => void;
  getOptionLabel?: (option: any) => string;
  loading?: boolean;
  emptyMessage?: string;
}

const CustomList: FC<ICustomList> = (props) => {
  const {
    title,
    items,
    loading = false,
    values = [],
    onChange,
    getOptionLabel,
    emptyMessage,
  } = props;

  const classes = useStyles();

  const numberOfChecked = (items: any[]) => intersection(values, items).length;

  const handleToggleAll = (items: any[]) => () => {
    if (numberOfChecked(items) === items.length) {
      onChange && onChange(not(values, items));
    } else {
      onChange && onChange(union(values, items));
    }
  };

  const handleToggle = (value: any) => () => {
    const currentIndex = values.indexOf(value);
    const newChecked = [...values];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    onChange && onChange(newChecked);
  };

  const ListItems = useMemo<JSX.Element[]>(
    () =>
      items.map((item: any, index: number) => {
        const labelId = `transfer-list-all-item-${item}-label`;

        return (
          <ListItem
            button
            disableRipple
            key={`transfer-list-item-${index}`}
            role="listitem"
            onClick={handleToggle(item)}>
            <ListItemIcon key={`transfer-list-item-icon-${index}`}>
              <Checkbox
                key={`transfer-list-item-checkbox-${index}`}
                disableRipple
                checked={values.indexOf(item) !== -1}
                tabIndex={-1}
                inputProps={{ 'aria-labelledby': labelId }}
              />
            </ListItemIcon>
            <ListItemText
              key={`transfer-list-item-text-${index}`}
              id={labelId}
              primary={getOptionLabel && getOptionLabel(item)}
            />
          </ListItem>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, values],
  );

  return (
    <Card>
      <CardHeader
        className={classes.cardHeader}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={
              numberOfChecked(items) === items.length && items.length !== 0
            }
            indeterminate={
              numberOfChecked(items) !== items.length &&
              numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            inputProps={{ 'aria-label': 'Tous les éléments sélectionnés' }}
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} sélectionné(s)`}
      />
      <Divider />
      {!items?.length && !loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          className={classes.list}>
          {emptyMessage || 'Vide'}
        </Box>
      ) : (
        <List className={classes.list} dense component="div" role="list">
          {loading ? (
            <Box className={classes.progressWrapper}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            ListItems
          )}
        </List>
      )}
    </Card>
  );
};

export default CustomList;
